import { cn } from '@heidi/utils';

/**
 * Density-driven size for the leading magnifying-glass icon.
 * Mirrors the existing accessory-icon convention used in Input's stories
 * (`size-4` for comfortable, `size-3` for compact).
 */
export const searchIconSizeClasses = {
  comfortable: 'size-4',
  compact: 'size-3',
} as const;

/**
 * Classes for the leading `Search` icon — pairs the density-driven size with
 * `shrink-0` so it never collapses when the input flexes narrower, and a
 * value-driven tint: `text-text-secondary` while the field is empty so it reads
 * as an accessory glyph, easing up to `text-text-primary` once a search term is
 * present to signal the field is active.
 */
export const searchIconClasses = (
  density: keyof typeof searchIconSizeClasses,
  active: boolean
) =>
  cn(
    searchIconSizeClasses[density],
    'shrink-0 transition-colors duration-150 ease-out',
    active ? 'text-text-primary' : 'text-text-secondary'
  );

/**
 * Suppress WebKit's native `::-webkit-search-cancel-button` so it doesn't
 * duplicate our own DS clear button. Firefox doesn't render one, so no
 * cross-browser branching is needed.
 */
export const suppressNativeSearchCancelClasses =
  '[&::-webkit-search-cancel-button]:appearance-none';

/**
 * Classes for the clear button — a ghost xxs iconOnly Button whose lucide `X`
 * inherits `currentColor` from this text tint.
 */
export const clearButtonClasses = 'text-text-secondary';
